import { CustomState } from "@iewccouk/Store/Reducers";
import DynamicFormFieldShell from "@iewccouk/Widgets/DynamicForm/DynamicFormFieldShell";
import DynamicFileUploadField from "@iewccouk/Widgets/DynamicForm/DynamicFileUploadField";
import { useRegisterDynamicFormAttachments } from "@iewccouk/Widgets/DynamicForm/useRegisterDynamicFormAttachments";
import { useRegisterDynamicFormFieldKeys } from "@iewccouk/Widgets/DynamicForm/useRegisterDynamicFormFieldKeys";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const MAX_UPLOAD_SLOTS = 5;
const DEFAULT_UPLOAD_SLOTS = "2";

const enum fields {
    numberOfUploadSlots = "numberOfUploadSlots",
    fieldKeyOne = "fieldKeyOne",
    fieldKeyTwo = "fieldKeyTwo",
    fieldKeyThree = "fieldKeyThree",
    fieldKeyFour = "fieldKeyFour",
    fieldKeyFive = "fieldKeyFive",
    heading = "heading",
    copy = "copy",
    labelOne = "labelOne",
    labelTwo = "labelTwo",
    labelThree = "labelThree",
    labelFour = "labelFour",
    labelFive = "labelFive",
}

const slotFieldKeys = [
    fields.fieldKeyOne,
    fields.fieldKeyTwo,
    fields.fieldKeyThree,
    fields.fieldKeyFour,
    fields.fieldKeyFive,
] as const;

const slotLabels = [
    fields.labelOne,
    fields.labelTwo,
    fields.labelThree,
    fields.labelFour,
    fields.labelFive,
] as const;

interface OwnProps extends WidgetProps {
    fields: {
        [fields.numberOfUploadSlots]: string;
        [fields.fieldKeyOne]: string;
        [fields.fieldKeyTwo]: string;
        [fields.fieldKeyThree]: string;
        [fields.fieldKeyFour]: string;
        [fields.fieldKeyFive]: string;
        [fields.heading]: string;
        [fields.copy]: string;
        [fields.labelOne]: string;
        [fields.labelTwo]: string;
        [fields.labelThree]: string;
        [fields.labelFour]: string;
        [fields.labelFive]: string;
    };
}

/** Maps Dynamic Form Redux state into widget props. */
const mapStateToProps = (state: CustomState, ownProps: OwnProps) => {
    const keys = slotFieldKeys.map((key, index) => ownProps.fields[key] || `fileUpload${index + 1}`);
    return {
        slotKeys: keys,
        storedFileValues: keys.map(key => state.customDynamicForm?.fieldValues[key]),
        formCleared: Object.keys(state.customDynamicForm?.fieldValues || {}).length === 0,
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

/** White card, matching Contact Us / Request a Quote file upload. */
const uploadSection: InjectableCss = {
    css: css`
        padding: 20px 30px;
        background-color: #fff;
        box-sizing: border-box;
    `,
};

const container: GridContainerProps = {
    gap: 20,
};

const gridItem: GridItemProps = {
    width: 12,
    css: css`
        flex-direction: column;
    `,
};

const headingText: TypographyPresentationProps = {
    weight: 700,
    size: "18px",
};

const widgetGroup = "SalesForce Dynamic" as any;

const parseSlotCount = (value: string | undefined) =>
    Math.min(MAX_UPLOAD_SLOTS, Math.max(1, parseInt(value || DEFAULT_UPLOAD_SLOTS, 10) || 2));

const isSlotVisible = (item: HasFields, slotIndex: number) =>
    parseSlotCount(item.fields[fields.numberOfUploadSlots]) >= slotIndex;

/** Salesforce Dynamic Form PDF upload section widget. */
const DynamicUploadInformation: React.FC<Props> = ({
    id,
    fields: widgetFields,
    slotKeys,
    storedFileValues,
    formCleared,
}) => {
    const { heading, copy } = widgetFields;
    const slotCount = parseSlotCount(widgetFields.numberOfUploadSlots);
    const labels = [
        widgetFields.labelOne,
        widgetFields.labelTwo,
        widgetFields.labelThree,
        widgetFields.labelFour,
        widgetFields.labelFive,
    ];

    const slots = slotKeys.slice(0, slotCount).map((fieldKey, index) => ({
        fieldKey,
        label: labels[index] || `Upload file ${index + 1}`,
        storedFileValue: storedFileValues[index],
    }));

    useRegisterDynamicFormFieldKeys(
        id,
        slots.map(slot => slot.fieldKey),
    );
    useRegisterDynamicFormAttachments(
        id,
        slots.map(slot => ({ fileKey: slot.fieldKey, nameKey: `${slot.fieldKey}Name` })),
    );

    return (
        <DynamicFormFieldShell>
            <StyledWrapper {...uploadSection}>
                <GridContainer {...container}>
                    <GridItem {...gridItem}>
                        <Typography {...headingText}>{heading}</Typography>
                    </GridItem>
                    <GridItem {...gridItem}>
                        <StyledWrapper>{parse(copy, parserOptions)}</StyledWrapper>
                    </GridItem>
                    {slots.map(slot => (
                        <GridItem key={slot.fieldKey} {...gridItem}>
                            <DynamicFileUploadField
                                widgetId={id}
                                fieldKey={slot.fieldKey}
                                label={slot.label}
                                isRequired={false}
                                storedFileValue={slot.storedFileValue}
                                formCleared={formCleared}
                                skipRegistration
                            />
                        </GridItem>
                    ))}
                </GridContainer>
            </StyledWrapper>
        </DynamicFormFieldShell>
    );
};

const slotCountOptions = Array.from({ length: MAX_UPLOAD_SLOTS }, (_, index) => {
    const value = String(index + 1);
    return { displayName: value, value };
});

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(DynamicUploadInformation),
    definition: {
        group: widgetGroup,
        displayName: "Upload Information",
        icon: "upload",
        fieldDefinitions: [
            {
                name: fields.numberOfUploadSlots,
                displayName: "Number of upload fields",
                editorTemplate: "DropDownField",
                fieldType: "General",
                options: slotCountOptions,
                defaultValue: DEFAULT_UPLOAD_SLOTS,
                isRequired: true,
                dependentFields: [...slotFieldKeys.slice(1), ...slotLabels.slice(1)],
            },
            {
                name: fields.fieldKeyOne,
                displayName: "Upload slot 1 key",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "",
                isRequired: true,
            },
            {
                name: fields.fieldKeyTwo,
                displayName: "Upload slot 2 key",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "",
                isRequired: true,
                isVisible: (item: HasFields) => isSlotVisible(item, 2),
            },
            {
                name: fields.fieldKeyThree,
                displayName: "Upload slot 3 key",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "",
                isRequired: true,
                isVisible: (item: HasFields) => isSlotVisible(item, 3),
            },
            {
                name: fields.fieldKeyFour,
                displayName: "Upload slot 4 key",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "",
                isRequired: true,
                isVisible: (item: HasFields) => isSlotVisible(item, 4),
            },
            {
                name: fields.fieldKeyFive,
                displayName: "Upload slot 5 key",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "",
                isRequired: true,
                isVisible: (item: HasFields) => isSlotVisible(item, 5),
            },
            {
                name: fields.heading,
                displayName: "Heading",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
                isRequired: true,
            },
            {
                name: fields.copy,
                displayName: "Copy",
                editorTemplate: "RichTextField",
                fieldType: "Translatable",
                defaultValue: "",
                isRequired: true,
            },
            {
                name: fields.labelOne,
                displayName: "Upload field 1 label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
                isRequired: true,
            },
            {
                name: fields.labelTwo,
                displayName: "Upload field 2 label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
                isRequired: true,
                isVisible: (item: HasFields) => isSlotVisible(item, 2),
            },
            {
                name: fields.labelThree,
                displayName: "Upload field 3 label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
                isRequired: true,
                isVisible: (item: HasFields) => isSlotVisible(item, 3),
            },
            {
                name: fields.labelFour,
                displayName: "Upload field 4 label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
                isRequired: true,
                isVisible: (item: HasFields) => isSlotVisible(item, 4),
            },
            {
                name: fields.labelFive,
                displayName: "Upload field 5 label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
                isRequired: true,
                isVisible: (item: HasFields) => isSlotVisible(item, 5),
            },
        ],
    },
};

export default widgetModule;
